import { Show, createMemo, type JSX } from "solid-js";
import { cx } from "../../../styled-system/css";
import { CoordCard } from "../../components/CoordCard";
import { VirtualGrid } from "../../components/VirtualGrid";
import { CoordList } from "../catalog/list/CoordList";
import { styles } from "../../styles";
import type { ColorFilters, CoordEntry, CoordItem } from "../../types";
import type { GridColumns, ViewMode } from "../preferences/types";
import type { FriendCode, FriendCodeListItem } from "./types";
import { friendDisplayEntry } from "./selectors";

function displayDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "日時不明" : date.toLocaleString("ja-JP");
}

function friendThumbnailUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || /\/thumb\/[^/?#]+(?:[?#]|$)/.test(trimmed)) return trimmed;
  return trimmed.replace(/([^/?#]+)([?#].*)?$/, "thumb/$1$2");
}

type Props = {
  items: FriendCodeListItem[];
  totalCount: number;
  loading: boolean;
  error?: unknown;
  canRegister: boolean;
  viewMode: ViewMode;
  gridColumns: GridColumns;
  isSelected: (item: CoordItem) => boolean;
  showItemQr: boolean;
  qrCycleIntervalMs: number;
  onToggle: (item: CoordItem) => void;
  onPreviewItem: (item: CoordItem) => void;
  onPreviewRecord: (record: FriendCodeListItem, entry: CoordEntry) => void;
  isOwnershipPending: (item: CoordItem) => boolean;
  onOwnershipChange: (item: CoordItem, owned: boolean) => void;
  onQrOpen: (item: CoordItem) => void;
  onRegister: () => void;
  onEdit: (code: FriendCode) => void;
  onScrollPositionChange: (scrolled: boolean) => void;
  toolbar: JSX.Element;
  colorFilters: ColorFilters;
};

export function FriendCodeList(props: Props) {
  const entries = createMemo(() => props.items.map(friendDisplayEntry));
  const recordByEntryKey = createMemo(() => new Map(props.items.map((record) => [`friend:${record.key}`, record])));
  const previewEntry = (entry: CoordEntry) => {
    const record = recordByEntryKey().get(entry.key);
    if (record) props.onPreviewRecord(record, entry);
  };

  return (
    <main class={props.viewMode === "list" ? cx(styles.mainList, styles.friendMainList) : styles.main}>
      <Show when={props.viewMode !== "list"}>{props.toolbar}</Show>
      <Show when={props.canRegister}>
        <div class={styles.friendToolbar}>
          <button class={styles.mixRegistrationButton} type="button" onClick={props.onRegister}>フレンドコード登録</button>
        </div>
      </Show>
      <Show when={props.error}>
        <div class={styles.alert} role="alert">フレンドコードを読み込めませんでした: {String(props.error)}</div>
      </Show>
      <Show when={!props.loading && !props.error && props.items.length === 0}>
        <p class={styles.empty}>{props.totalCount === 0
          ? "登録済みのフレンドコードはありません。"
          : "条件に一致するフレンドコードはありません。検索条件を変更してください。"}</p>
      </Show>
      <Show
        when={props.viewMode !== "list"}
        fallback={<CoordList
          entries={entries()}
          isSelected={props.isSelected}
          onToggle={props.onToggle}
          canUpdateOwnership={props.canRegister}
          showItemQr={props.showItemQr}
          qrCycleIntervalMs={props.qrCycleIntervalMs}
          isOwnershipPending={props.isOwnershipPending}
          onOwnershipChange={props.onOwnershipChange}
          onQrOpen={props.onQrOpen}
          onPreviewEntry={previewEntry}
          onScrollPositionChange={props.onScrollPositionChange}
          toolbar={props.toolbar}
          colorFilters={props.colorFilters}
        />}
      >
        <VirtualGrid
          items={props.items}
          columns={props.gridColumns}
          mode="grid"
          class={styles.grid}
          busy={props.loading}
          getKey={(record) => record.key}
        >
          {(record) => {
            const entry = () => friendDisplayEntry(record);
            return <CoordCard
              entry={entry()}
              title={record.playerName}
              metadata={<p class={styles.meta}>
                ID: {record.playerId || "-"}　photo_seq: {record.code.photoSeq || "-"}　コーデ: {record.coordinateName}　登録: {displayDate(record.code.createdAt)}
                <Show when={record.code.tags.not_web}>　webフレカではない</Show>
              </p>}
              actions={<div class={styles.friendCardActions}>
                <button class={styles.secondaryButton} type="button" onClick={() => props.onEdit(record.code)}>コーデ紐づけ変更</button>
              </div>}
              showParentImage
              parentImageThumbnailUrl={friendThumbnailUrl(record.code.imageUrl)}
              previewParentOnClick
              isSelected={props.isSelected}
              onToggle={props.onToggle}
              onPreviewEntry={() => props.onPreviewRecord(record, entry())}
              onPreviewItem={props.onPreviewItem}
              canUpdateOwnership={props.canRegister}
              showItemQr={props.showItemQr}
              isOwnershipPending={props.isOwnershipPending}
              onOwnershipChange={props.onOwnershipChange}
              onQrRegistration={props.onQrOpen}
              colorFilters={props.colorFilters}
            />;
          }}
        </VirtualGrid>
      </Show>

    </main>
  );
}
